import { Box, Container, IconButton, Link, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import InstagramIcon from '@mui/icons-material/Instagram';
import type { ConfiguracaoLoja, IdentificadorLoja } from '../api';
import { montarLinkInstagram } from '../lib/contato';
import { rotuloTipoPedido } from '../lib/canal';
import { SobreCardapio } from './sobre-cardapio';
import { fundoRodape } from '../theme/superficie';

/** Site institucional do Sischef (assinatura da plataforma no rodapé). Sem
 * `www`: o certificado não cobre o subdomínio. */
const URL_SISCHEF = 'https://sischef.com';

/**
 * Rodapé do cardápio: assinatura da loja (nome + Instagram, se houver), crédito
 * da plataforma e o tipo de pedido atual. Usado pelos canais de celular/desktop
 * — os aparelhos fixos da loja (totem/tablet) usam o layout kiosk, que não tem
 * rodapé e leva o "Sobre" na própria tela.
 */
export function RodapeLoja({
  config,
  tipo,
}: {
  config: ConfiguracaoLoja;
  tipo: IdentificadorLoja['tipo'];
}): JSX.Element {
  const instagram = config.instagram?.trim();
  const ano = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={(tema) => ({
        position: 'relative',
        mt: 6,
        py: 4,
        color: 'text.secondary',
        background: fundoRodape(tema),
        // Fio da marca no alto, sumindo nas pontas: o vermelho vira acento, não faixa
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '15%',
          right: '15%',
          height: 2,
          background: `linear-gradient(90deg, transparent, ${alpha(tema.palette.primary.main, 0.55)}, transparent)`,
        },
      })}
    >
      <Container maxWidth="lg">
        <Stack spacing={1.5} alignItems="center" textAlign="center">
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {config.nome}
            </Typography>
            {instagram && (
              <IconButton
                href={montarLinkInstagram(instagram)}
                target="_blank"
                rel="noopener"
                aria-label="Instagram"
                size="small"
                color="primary"
              >
                <InstagramIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>

          <Box sx={{ width: 40, height: '1px', bgcolor: 'divider' }} />

          <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center">
            <Typography variant="body2">Plataforma fornecida por</Typography>
            <Link
              href={URL_SISCHEF}
              target="_blank"
              rel="noopener"
              underline="hover"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 700 }}
            >
              <Box component="img" src="/logo-sischef.svg" alt="" sx={{ width: 16, height: 16 }} />
              Sischef
            </Link>
          </Stack>

          {/* "Sobre o Sischef Cardápio" — mesmo conteúdo em todos os modos. */}
          <SobreCardapio posicao="rodape" />

          {/* Tipo do pedido atual — padronizado no final do rodapé p/ todos os canais. */}
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            {rotuloTipoPedido(tipo)} · © {ano} {config.nome}. Todos os direitos reservados
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
}
